import { useEffect, useState, useRef } from 'react';
import { EnvironmentOutlined } from '@ant-design/icons';
import { Input, Radio, Select } from 'antd';
import cn from 'classnames';
import { useFormikContext } from 'formik';
import { observer } from 'mobx-react';

import { ReactComponent as ExpandMoreSVG } from '../../assets/img/expand-more.svg';
import ImageUpload from '../ImageUpload/ImageUpload';
import TagCreator from '../TagCreator/TagCreator';
import {
  BNS3_CHARITY_LOT_CATEGORY_ID,
  BNS3_SERVICE_PRODUCT_TYPE,
  BNS3_OTHER_LOT_CATEGORY_ID,
  MAX_PRICE,
  DIGIT_REGEXP,
  MIN_DESCRIPTION_LENGTH,
  MAX_DESCRIPTION_LENGTH,
} from '../../constants';
import { addImageIndex, getImageIndex, renderError, stripHtml } from '../../helpers/utils';
import { lotStateEnum, lotTypesEnum } from 'constants/enums';
import { useStores } from 'stores';
import styles from './styles.module.scss';

const LotForm = ({
  setEditorState,
  editorState,
  isEdit,
}) => {
  const {
    singleLotStore: {
      getCategories,
      getProductTypes,
      productTypes,
      getLotTypes,
      lotTypes,
      lotCategories,
      getTags,
      tags,
    },
    userStore: { currencies, getLocations, getCurrencies, locations },
  } = useStores();
  const [subcategories, setSubcategories] = useState([]);
  const previousSubmitCountRef = useRef(0);
  const scrollTimeoutRef = useRef(null);
  const hasScrolledForSubmissionRef = useRef(false);

  const { handleBlur, setFieldValue, touched, values, errors, setFieldTouched, submitCount } = useFormikContext();

  const { TextArea } = Input;

  const [textDescription, setTextDescription] = useState('');

  useEffect(() => {
    getCategories();
    getProductTypes();
    getLotTypes();
    getLocations();
    getCurrencies();
    getTags();
  }, [getCategories, getProductTypes, getLotTypes, getLocations, getCurrencies, getTags]);

  useEffect(() => {
    if (submitCount > 0) {
      setFieldTouched('description', true, true);
    }
  }, [submitCount, setFieldTouched]);

  useEffect(() => {
    const selectedCategoryInstance = lotCategories.find(({ id }) => id === values.categoryId);
    if (selectedCategoryInstance) {
      setSubcategories(selectedCategoryInstance.subCategories);
    }

    setTextDescription(values.description || '');
  }, [lotCategories, values.categoryId, values.description]);

  const cleanScrollTimeout = () => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
  };

  const scrollToError = () => {
    const invalidEl = document.querySelector('.is-invalid, .is-invalid-container');
    if (invalidEl) {
      invalidEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      invalidEl.focus?.();
      return;
    }

    const firstErrorKey = Object.keys(errors)[0];
    if (!firstErrorKey) return;

    const selectors = [
      `[name="${firstErrorKey}"]`,
      `#${firstErrorKey}`,
      `[id="${firstErrorKey}"]`,
    ];

    selectors.forEach(sel => {
      const el = document.querySelector(sel);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus?.();
    });
  };

  useEffect(() => {
    const isNewSubmission = submitCount > previousSubmitCountRef.current;
    
    if (isNewSubmission) {
      hasScrolledForSubmissionRef.current = false;
      previousSubmitCountRef.current = submitCount;
    }

    if (isNewSubmission && Object.keys(errors).length > 0 && !hasScrolledForSubmissionRef.current) {
      hasScrolledForSubmissionRef.current = true;
      scrollTimeoutRef.current = setTimeout(scrollToError, 100);
    }

    return () => {
      cleanScrollTimeout();
    };
  }, [submitCount, errors]);

  const onEditorStateChange = newEditorState => {
    setEditorState(newEditorState);
    const content = (newEditorState && (newEditorState.target?.value ?? newEditorState.currentTarget?.value ?? '')) || '';
    const stringContent = stripHtml(content).trim();

    setTextDescription(content);

    setFieldValue('description', stringContent, true);
    setTimeout(() => setFieldTouched('description', true, false), 0);
  };

  const onDescriptionBlur = () => {
    const stringContent = stripHtml(textDescription || '').trim();
    setFieldValue('description', stringContent, true);
    setFieldTouched('description', true, true);
  };

  const isSubcategoryShown =
    values.subcategoryId &&
    values.categoryId !== BNS3_OTHER_LOT_CATEGORY_ID &&
    values.categoryId !== BNS3_CHARITY_LOT_CATEGORY_ID;

  const isSubcategoryFieldDisabled =
    !values.categoryId ||
    values.categoryId === BNS3_OTHER_LOT_CATEGORY_ID ||
    values.categoryId === BNS3_CHARITY_LOT_CATEGORY_ID;

  const handlePickMainImage = (imagesList, _, index) => {
    const reordered = [...imagesList];
    const selectedIdx = reordered.findIndex(img => img.index === index);
    if (selectedIdx > 0) {
      const [picked] = reordered.splice(selectedIdx, 1);
      reordered.unshift(picked);
    }
    const reindexed = addImageIndex(reordered);
    setFieldValue('mainImageIndex', 0);
    setFieldValue(isEdit ? 'lotImagesToAdd' : 'lotImages', reindexed);
  };

  const handleAddImages = fileList => {
    const reindexed = addImageIndex(fileList);
    setFieldValue('mainImageIndex', 0);
    setFieldValue(isEdit ? 'lotImagesToAdd' : 'lotImages', reindexed);
  };

  const removeImageFromDB = id => setFieldValue('imagesIdsToDelete', [...(values.imagesIdsToDelete || []), id]);

  const handleProductTypeChange = newValue => {
    setFieldValue('productType', newValue);
    if (newValue === BNS3_SERVICE_PRODUCT_TYPE) {
      setFieldValue('lotState', 0);
    } else {
      setFieldValue('lotState', '');
      setFieldTouched('lotState', false, false);
    }
  };

  const handleCategoryChange = newValue => {
    setFieldValue('categoryId', newValue);
    if (newValue === BNS3_OTHER_LOT_CATEGORY_ID || newValue === BNS3_CHARITY_LOT_CATEGORY_ID) {
      setFieldValue('subcategoryId', newValue);
    } else {
      setFieldValue('subcategoryId', '');
    }
    if (newValue === BNS3_CHARITY_LOT_CATEGORY_ID) {
      setFieldValue('fixedPrice', 0);
      setFieldTouched('fixedPrice', false, false);
    } else {
      setFieldValue('fixedPrice', '');
    }
  };

  function handleDigitInputChange(e) {
    let val = e?.target?.value ?? '';
    if (typeof val === 'string') val = val.replace(',', '.');

    if (val === '') {
      setFieldValue('fixedPrice', '', true);
      setFieldTouched('fixedPrice', false, false);
      return;
    }

    if (val.match(DIGIT_REGEXP)) {
      setFieldValue('fixedPrice', val, true);
      setFieldTouched('fixedPrice', true, false)
    }
  }

  return (
    <form className={styles.bns3_form_container}>
      <h2 className={cn(styles.bns3_block_title, styles.bns3_lot_type_title)}>lot type</h2>

      <div className={styles.bns3_inputs_container}>
        <div className={styles.bns3_flex_container}>
          <div className={styles.bns3_input_wrapper}>
            <label className={styles.bns3_label} htmlFor="lotType">
              Type of lot
              <span className={styles.bns3_required_sign}>&#42;</span>
            </label>
            <Select
              id="lotType"
              name="lotType"
              placeholder="Select.."
              className={cn(styles.bns3_form_selector, styles.bns3_form_lot_type_selector, {
                'is-invalid': errors.lotType && touched.lotType,
              })}
              popupClassName={styles.bns3_select_dropdown}
              size="large"
              onChange={newValue => setFieldValue('lotType', newValue)}
              onBlur={handleBlur}
              value={values.lotType}
              suffixIcon={<ExpandMoreSVG />}
            >
              {lotTypes.map(type => (
                <Select.Option
                  key={`lotType${type}`}
                  value={type}
                  label={lotTypesEnum[type]}
                  className={styles.bns3_dropdown_option}
                >
                  {lotTypesEnum[type]}
                </Select.Option>
              ))}
            </Select>
            {renderError(errors, 'lotType', touched, submitCount)}
          </div>

          <div className={styles.bns3_input_wrapper}>
            <label className={styles.bns3_label} htmlFor="productType">
              Product type
              <span className={styles.bns3_required_sign}>&#42;</span>
            </label>
            <Select
              id="productType"
              name="productType"
              placeholder="Select.."
              className={cn(styles.bns3_form_selector, styles.bns3_form_product_type_selector, {
                'is-invalid': errors.productType && touched.productType,
              })}
              popupClassName={styles.bns3_select_dropdown}
              size="large"
              onChange={handleProductTypeChange}
              onBlur={handleBlur}
              suffixIcon={<ExpandMoreSVG />}
              value={values.productType}
            >
              {productTypes.map(productType => (
                <Select.Option
                  key={`productType${productType}`}
                  value={productType}
                  label={productType}
                  className={styles.bns3_dropdown_option}
                >
                  {productType}
                </Select.Option>
              ))}
            </Select>
            {renderError(errors, 'productType', touched, submitCount)}
          </div>
        </div>

        <div className={styles.bns3_flex_container}>
          <div className={styles.bns3_input_wrapper}>
            <label className={styles.bns3_label} htmlFor="categoryId">
              Categories
              <span className={styles.bns3_required_sign}>&#42;</span>
            </label>
            <Select
              id="categoryId"
              name="categoryId"
              placeholder="Select category.."
              className={cn(styles.bns3_form_selector, styles.bns3_form_categoty_selector, {
                'is-invalid': errors.categoryId && touched.categoryId,
              })}
              popupClassName={styles.bns3_select_dropdown}
              size="large"
              onChange={handleCategoryChange}
              onBlur={handleBlur}
              suffixIcon={<ExpandMoreSVG />}
              value={values.categoryId}
            >
              {lotCategories.slice(1, lotCategories.length).map(category => (
                <Select.Option
                  key={`category${category.id}`}
                  value={category.id}
                  label={category.name}
                  className={styles.bns3_dropdown_option}
                >
                  {category.name}
                </Select.Option>
              ))}
            </Select>
            {renderError(errors, 'categoryId', touched, submitCount)}
          </div>

          <div className={styles.bns3_input_wrapper}>
            <label className={styles.bns3_label} htmlFor="subcategories">
              Subcategories
              <span className={styles.bns3_required_sign}>&#42;</span>
            </label>
            <Select
              id="subcategoryId"
              name="subcategoryId"
              placeholder="Select subcategory.."
              className={cn(styles.bns3_form_selector, styles.bns3_form_subcategory_selector, {
                'is-invalid': errors.subcategoryId && touched.subcategoryId && !isSubcategoryFieldDisabled,
              })}
              popupClassName={styles.bns3_select_dropdown}
              size="large"
              onChange={newValue => setFieldValue('subcategoryId', newValue)}
              value={isSubcategoryShown ? values.subcategoryId : null}
              disabled={isSubcategoryFieldDisabled}
              onBlur={handleBlur}
              suffixIcon={<ExpandMoreSVG />}
            >
              {subcategories.length > 0 &&
                subcategories.map(subcategory => (
                  <Select.Option
                    key={`subcategory${subcategory.id}`}
                    value={subcategory.id}
                    label={subcategory.name}
                    className={styles.bns3_dropdown_option}
                  >
                    {subcategory.name}
                  </Select.Option>
                ))}
            </Select>
            {!isSubcategoryFieldDisabled && renderError(errors, 'subcategoryId', touched, submitCount)}
          </div>
        </div>

        {values.productType !== BNS3_SERVICE_PRODUCT_TYPE && (
          <div className={styles.bns3_input_container}>
            <label className={styles.bns3_label} htmlFor="lotState">
              Lot State
              <span className={styles.bns3_required_sign}>&#42;</span>
            </label>
            <Radio.Group
              id="lotState"
              name="lotState"
              onChange={e => setFieldValue('lotState', e.target.value)}
              onBlur={handleBlur}
              className={cn(styles.bns3_lot_state_group, {
                'is-invalid': errors.lotState && touched.lotState,
              })}
              value={values.lotState}
            >
              {Object.keys(lotStateEnum).map((key, value) => (
                <Radio key={`lotState${key}`} value={value} className={styles.bns3_lot_state_item}>
                  {key}
                </Radio>
              ))}
            </Radio.Group>

            {renderError(errors, 'lotState', touched, submitCount)}
          </div>
        )}

        <div className={styles.bns3_input_container}>
          <TagCreator initialTags={tags} handleBlur={handleBlur} error={errors.tagIds && touched.tagIds} />
          {renderError(errors, 'tagIds', touched, submitCount)}
        </div>
      </div>
      <h2 className={cn(styles.bns3_block_title, styles.bns3_lot_information_title)}>information</h2>

      <div className={styles.bns3_inputs_container}>
        <div className={styles.bns3_input_container}>
          <label className={styles.bns3_label} htmlFor="lot-name">
            Lot name
            <span className={styles.bns3_required_sign}>&#42;</span>
          </label>
          <Input
            id="lot-name"
            name="title"
            placeholder="Enter lot name.."
            type="text"
            onChange={e => {
              setFieldValue('title', e.target.value, true);
              setTimeout(() => setFieldTouched('title', true, false), 0);
            }}
            onBlur={handleBlur}
            value={values.title}
            className={cn(styles.bns3_input, styles.bns3_input_lot_name, {
              'is-invalid': errors.title && touched.title,
            })}
          />
          {renderError(errors, 'title', touched, submitCount)}
        </div>

        <div className={styles.bns3_flex_container}>
          <div className={styles.bns3_input_wrapper}>
            <label className={styles.bns3_label} htmlFor="locationId">
              Location
              <span className={styles.bns3_required_sign}>&#42;</span>
            </label>
            <Select
              id="locationId"
              name="locationId"
              optionLabelProp="label"
              className={cn(styles.bns3_form_selector, styles.bns3_form_location_selector, {
                'is-invalid': errors.locationId && touched.locationId,
              })}
              popupClassName={styles.bns3_select_dropdown}
              onChange={newValue => setFieldValue('locationId', newValue)}
              onBlur={handleBlur}
              value={values.locationId}
              suffixIcon={<ExpandMoreSVG />}
            >
              {locations.map(location => (
                <Select.Option
                  key={`location${location.id}`}
                  value={location.id}
                  label={
                    <div className={styles.bns3_select_location_label}>
                      <EnvironmentOutlined className={styles.bns3_select_location_icon} />
                      {location.name}
                    </div>
                  }
                  className={styles.bns3_dropdown_option}
                >
                  {location.name}
                </Select.Option>
              ))}
            </Select>
            {renderError(errors, 'locationId', touched, submitCount)}
          </div>

          <div className={styles.bns3_input_wrapper}>
            <div className={styles.bns3_price_selection_container}>
              <div>
                <label className={styles.bns3_label}>
                  Price
                  <span className={styles.bns3_required_sign}>&#42;</span>
                </label>
                <Input
                  name="fixedPrice"
                  placeholder={values.categoryId !== BNS3_CHARITY_LOT_CATEGORY_ID ? 'Enter value' : ''}
                  step="10"
                  min={0}
                  max={MAX_PRICE}
                  disabled={values.categoryId === BNS3_CHARITY_LOT_CATEGORY_ID}
                  inputMode="numeric"
                  onChange={handleDigitInputChange}
                  onBlur={handleBlur}
                  value={(values.categoryId !== BNS3_CHARITY_LOT_CATEGORY_ID && values.fixedPrice !== '0') ? values.fixedPrice : ''}
                  className={cn(styles.bns3_input, styles.bns3_input_lot_price, {
                    'is-invalid': errors.fixedPrice && touched.fixedPrice,
                  })}
                />
              </div>

              {currencies && (
                <Select
                  name="currency"
                  className={cn(styles.bns3_form_selector, styles.bns3_form_currency_selector)}
                  popupClassName={styles.bns3_select_dropdown}
                  onChange={newValue => setFieldValue('currency', newValue)}
                  onBlur={handleBlur}
                  value={values.currency}
                  suffixIcon={<ExpandMoreSVG />}
                >
                  {currencies.map(currency => (
                    <Select.Option
                      key={`currency${currency}`}
                      value={currency}
                      label={<div className={styles.bns3_filters_select_location_label}>{currency}</div>}
                      className={styles.bns3_dropdown_option}
                    >
                      {currency}
                    </Select.Option>
                  ))}
                </Select>
              )}
            </div>
            {/* This check below is needed for displaying no errors after changing category in real time */}
            {values.categoryId !== BNS3_CHARITY_LOT_CATEGORY_ID && renderError(errors, 'fixedPrice', touched, submitCount)}
          </div>
        </div>

        <div className={styles.bns3_input_container}>
          <p className={styles.bns3_label}>
            Description
            <span className={styles.bns3_required_sign}>&#42;</span>
          </p>
          <TextArea
            editorState={editorState}
            id="text"
            name="description"
            type="text"
            label="Description"
            placeholder="Tell people more about your lot..."
            variant="outlined"
            onChange={onEditorStateChange}
            onBlur={onDescriptionBlur}
            value={textDescription}
            color="primary"
            size="small"
            minLength={MIN_DESCRIPTION_LENGTH}
            maxLength={MAX_DESCRIPTION_LENGTH}
            status={errors.description && touched.description ? 'error' : undefined}
            autoSize={{ minRows: 5, maxRows: 10 }}
            className={cn('col-7 ml-3 ml-sm-0 col-sm-9 col-lg-10 form-control', styles.bns3_description_textarea, {
              'is-invalid': errors.description && touched.description,
            })}
          />
          {renderError(errors, 'description', touched, submitCount)}
        </div>

        <div className={styles.bns3_input_container}>
          <p className={styles.bns3_label}>Images</p>
          <ImageUpload
            imagesList={isEdit ? values.lotImagesToAdd : values.lotImages}
            uploadStore={handleAddImages}
            mainImageIndex={values.mainImageIndex}
            handlePickMainImage={handlePickMainImage}
            removeImageFromDB={removeImageFromDB}
          />
        </div>
      </div>
    </form>
  );
};

export default observer(LotForm);
